/**
 * Der Abschnitt **Ticketsystem** (#112): die Instanz und die
 * **Zuordnungsregeln**, mit denen ein Ticketverweis im Commit-Kommentar seine
 * Ticketliste findet.
 *
 * **Er ist die Stelle, die „warum ist Dev435 kein Link?" beantwortet.** Deshalb
 * steht die Instanz hier mit im Bild, obwohl sie sich hier nicht setzen lässt —
 * sie kommt aus der Konfiguration. Fehlt sie oder fehlt eine Regel, sagt der
 * Abschnitt in einem Satz, was fehlt und was dadurch nicht geht: Eine Maske, die
 * gepflegt aussieht und trotzdem nichts bewirkt, ist die unangenehmste Art, eine
 * fehlende Einstellung zu erfahren.
 *
 * **Die Vorsilbe wählt die Liste, die Zahl den Vorgang.** Verwiesen wird als
 * `Dev435` oder `QS580`; welche Liste `Dev` meint, ist nicht herzuleiten — `Dev`
 * ist nicht `dev-liste`, und die Schreibweise wechselt. Also steht die Bindung
 * hier und wird nicht geraten.
 *
 * **Abgewiesen wird, nicht zurechtgebogen.** Ein Ausdruck ohne genau eine
 * Fanggruppe und ein Ziel ohne die Form `owner/repo` kommen mit dem Grund im
 * Klartext zurück — die Prüfung steht **im Server**, hier steht nur ihre
 * Antwort. Es gibt keine Vorgabe, auf die zurückgefallen würde: Nummer 435 gibt
 * es in beiden Listen, und ein Link in die falsche führt zu einem existierenden,
 * fremden Vorgang.
 *
 * **Die Reihenfolge ist bedeutungstragend** und steht deshalb in der Liste: Sie
 * entscheidet, welche von zwei Regeln gewinnt, die an derselben Stelle desselben
 * Textes gleich lang treffen.
 *
 * **Das Kürzel steht hier, gehört aber der Ticketliste** (#131; ADR-0030): In der
 * Spalte „Ticket" heißt jeder Vorgang dieser Liste `DEV-<Nummer>`, gleich welche
 * Regel ihn gefunden hat. Zwei Regeln, die auf dieselbe Liste zeigen, teilen es —
 * das sagt der Hilfetext am Feld, denn es ist die Falle dieser Maske. Ohne Kürzel
 * steht in der Spalte `owner/repo#nummer`; abgeleitet wird keines.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  IconButton,
  Modal,
  TextField,
} from "../../design-system";
import type { AdminApi, IssueReferenceRule, IssueReferenceRuleDraft } from "../../api/client";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

export function IssueTrackerSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded(() => client.issueReferenceRules());
  const rules = state.kind === "loaded" ? state.value.rules : [];
  // `null` heißt: keine Instanz eingestellt. Der Abschnitt sagt dann, dass daran
  // kein Link entsteht — und zwar auch dem, der gerade eine Regel einträgt.
  const instance = state.kind === "loaded" ? state.value.forgejoBaseAddress : null;
  const [edited, setEdited] = useState<IssueReferenceRule | "neu" | null>(null);
  const [deleted, setDeleted] = useState<IssueReferenceRule | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const act = (action: Promise<unknown>): void => {
    setFailure(null);
    action.then(reload, (error: unknown) => setFailure(describe(error)));
  };

  return (
    <>
      <ListSection
        title="Ticketsystem"
        glyph="ticket"
        first
        headerRight={
          <span style={{ display: "flex", alignItems: "center", gap: "var(--sp-5)" }}>
            {state.kind === "loaded" ? (
              <Chip>{rules.length === 1 ? "eine Regel" : `${rules.length} Regeln`}</Chip>
            ) : null}
            <Button variant="outline" icon="plus" onClick={() => setEdited("neu")}>
              Regel anlegen
            </Button>
          </span>
        }
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
            padding: "var(--sp-2) var(--sp-5)",
          }}
        >
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {failure === null ? null : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
          {state.kind === "loaded" ? (
            <InstanceNotice instance={instance} rules={rules.length} />
          ) : null}
        </div>

        {state.kind === "loaded" && rules.length > 0 ? (
          <DataTable
            columns={[
              { key: "position", label: "Reihenfolge", align: "right", width: 100 },
              { key: "expression", label: "Ausdruck", width: 280, maxWidth: 280 },
              { key: "target", label: "Ticketliste", width: 200 },
              // Schmal: Ein Kürzel ist kurz, und die Wirkung daneben braucht den
              // Platz. Leer heißt „keines eingetragen" — ein „—" in der Hälfte
              // der Zeilen wäre Lärm.
              { key: "prefix", label: "Kürzel", width: 90, maxWidth: 90 },
              { key: "effect", label: "Wirkung" },
              { key: "actions", label: "", width: 70, align: "right" },
            ]}
            rows={rules.map((rule) => ({
              cells: {
                position: <NumericCell>{String(rule.position)}</NumericCell>,
                // Die tragende Spalte steht hier **nicht** vorn: Vorn steht die
                // Reihenfolge, wiedererkannt wird die Regel an ihrem Ausdruck.
                // Genau dafür gibt es die Hülle — `DataTable` hebt von sich aus
                // nur die erste Spalte an.
                expression: <StrongCell title={rule.expression}>{rule.expression}</StrongCell>,
                target: rule.target,
                prefix: rule.prefix ?? "",
                effect: <EffectCell rule={rule} instance={instance} />,
                actions: (
                  <span style={{ display: "inline-flex", gap: "var(--sp-3)" }}>
                    <IconButton
                      icon="pencil"
                      tip="Ändern"
                      size="sm"
                      tone="quiet"
                      onClick={() => setEdited(rule)}
                    />
                    <IconButton
                      icon="trash-2"
                      tip="Löschen"
                      size="sm"
                      tone="quiet"
                      onClick={() => setDeleted(rule)}
                    />
                  </span>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <RuleDialog
          rule={edited === "neu" ? null : edited}
          instance={instance}
          save={(draft) =>
            edited === "neu"
              ? client.createIssueReferenceRule(draft)
              : client.updateIssueReferenceRule(edited.id, draft)
          }
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}

      {deleted === null ? null : (
        <Modal onClose={() => setDeleted(null)}>
          <Dialog
            title="Zuordnungsregel löschen"
            description={`„${deleted.expression}" wird gelöscht. Ein Verweis dieser Form wird danach nicht mehr erkannt und bleibt Text; an abgelegten Zustellungen ändert sich nichts.`}
            width={520}
            onClose={() => setDeleted(null)}
            footer={
              <>
                <Button variant="outline" size="lg" onClick={() => setDeleted(null)}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    const id = deleted.id;
                    setDeleted(null);
                    act(client.deleteIssueReferenceRule(id));
                  }}
                >
                  Endgültig löschen
                </Button>
              </>
            }
          >
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              {`Ziel: ${deleted.target}`}
            </span>
          </Dialog>
        </Modal>
      )}
    </>
  );
}

/**
 * Was fehlt und was dadurch nicht geht — in einem Satz.
 *
 * Drei Lagen, drei Sätze. Der unangenehmste steht zuerst: gepflegte Regeln, die
 * trotzdem nichts bewirken, weil die Instanz fehlt. Er nennt deshalb den
 * Konfigurationsschlüssel, denn wer diesen Abschnitt offen hat, kann ihn nicht
 * setzen und muss wissen, wen er darum bittet.
 */
function InstanceNotice({
  instance,
  rules,
}: {
  instance: string | null;
  rules: number;
}): ReactElement {
  if (instance === null) {
    // Ohne Auszeichnung: Der Schlüsselname steht im Fließtext, denn diese
    // Oberfläche kennt nur eine Schrift, und ein `code` brächte eine zweite.
    return (
      <Notice
        glyph="circle-alert"
        color="var(--ac-amber)"
        text={
          "Es ist keine Instanz eingestellt: Aus einem Ticketverweis im Commit-Kommentar entsteht "
          + "kein Link, auch mit Regel nicht. Sie steht als forgejo_base_address in "
          + "config/configuration.php und lässt sich hier nicht setzen."
        }
      />
    );
  }

  if (rules === 0) {
    return (
      <Notice
        glyph="circle-alert"
        color="var(--ac-amber)"
        text={
          `Die Instanz ist ${instance}, aber es ist keine Regel angelegt: Kein Commit-Kommentar `
          + "ergibt einen Link. Eine nackte Nummer reicht nicht — dieselbe Nummer gibt es in "
          + "mehreren Ticketlisten, und ein Link in die falsche führt zu einem fremden Vorgang. "
          + "Eine vollständige Adresse dieser Instanz wird trotzdem erkannt: Sie trägt ihre "
          + "Ticketliste selbst."
        }
      />
    );
  }

  return (
    <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
      {`Die Instanz ist ${instance}. Aus einem Treffer wird ${instance}/<Ticketliste>/issues/<Nummer>; `
        + "eine vollständige Adresse im Kommentar behält ihre eigene Ticketliste. Beim Anzeigen "
        + "einer Revision wird das Ticketsystem nicht gefragt."}
    </span>
  );
}

/**
 * Wohin diese Regel führt — an einem Beispiel, das aus ihr selbst entsteht.
 *
 * Eine Regel, die **nichts erkennt**, sagt das an dieser Stelle und nicht nur im
 * Log: Sie kam von Hand in die Datenbank, denn die Maske weist so etwas ab.
 */
function EffectCell({
  rule,
  instance,
}: {
  rule: IssueReferenceRule;
  instance: string | null;
}): ReactElement {
  if (rule.complaint !== null) {
    return (
      <span style={{ color: "var(--ac-red)", fontSize: "var(--fs-11)" }}>{rule.complaint}</span>
    );
  }

  return (
    <span style={{ color: "var(--fg-6)", fontSize: "var(--fs-11)" }}>
      {`${instance ?? "<Instanz>"}/${rule.target}/issues/<Nummer>`}
    </span>
  );
}

function RuleDialog({
  rule,
  instance,
  save,
  close,
  saved,
}: {
  rule: IssueReferenceRule | null;
  instance: string | null;
  save: (draft: IssueReferenceRuleDraft) => Promise<IssueReferenceRule>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [expression, setExpression] = useState(rule?.expression ?? "");
  const [target, setTarget] = useState(rule?.target ?? "");
  const [prefix, setPrefix] = useState(rule?.prefix ?? "");
  const [position, setPosition] = useState(rule === null ? "" : String(rule.position));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);

    save({
      expression: expression.trim(),
      target: target.trim(),
      // Auch leer: Das ist hier die Auskunft, dass die Ticketliste kein Kürzel
      // haben soll — und dann steht in der Spalte `owner/repo#nummer`.
      prefix: prefix.trim(),
      // Keine Stelle genannt heißt: Beim Anlegen hinten, beim Ändern
      // unverändert. Eine Regel, die beim Speichern beiläufig eine andere
      // überholt, hätte das niemand verlangt.
      ...(position.trim() === "" ? {} : { position: Number(position) }),
    }).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title={rule === null ? "Zuordnungsregel anlegen" : "Zuordnungsregel ändern"}
        description="Eine Regel bindet einen Ausdruck an eine Ticketliste: Die Vorsilbe wählt die Liste, die Fanggruppe liefert die Nummer."
        width={560}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Ausdruck" htmlFor="rule-expression">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="rule-expression"
                value={expression}
                onChange={setExpression}
                placeholder="(?i)\bdev(\d+)\b"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Ein regulärer Ausdruck ohne Begrenzer, mit genau einer Fanggruppe — sie liefert die
                Nummer. „(?i)" erkennt unabhängig von Groß- und Kleinschreibung, „\b" hält
                revDev435 heraus. Eine Tilde wird als \~ geschrieben.
              </span>
            </div>
          </FormRow>

          <FormRow label="Ticketliste" htmlFor="rule-target">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="rule-target"
                value={target}
                onChange={setTarget}
                placeholder="yQos/dev-liste"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                {`Form: owner/repo. Daraus wird ${instance ?? "<Instanz>"}/owner/repo/issues/<Nummer>; `
                  + `in der Spalte „Ticket" steht ${prefix.trim() === "" ? "owner/repo#<Nummer>" : `${prefix.trim()}-<Nummer>`}.`}
              </span>
            </div>
          </FormRow>

          <FormRow label="Kürzel" htmlFor="rule-prefix">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="rule-prefix"
                value={prefix}
                onChange={setPrefix}
                placeholder="DEV"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Gilt für die ganze Ticketliste — auch für die andere Regel, die auf sie zeigt. Leer
                heißt: in der Spalte steht owner/repo#nummer. Buchstaben und Ziffern, beginnend mit
                einem Buchstaben, höchstens 16 Zeichen.
              </span>
            </div>
          </FormRow>

          <FormRow label="Reihenfolge" htmlFor="rule-position">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="rule-position"
                type="number"
                value={position}
                onChange={setPosition}
                placeholder="1"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Entscheidet bei zwei Regeln, die an derselben Stelle gleich lang treffen. Leer
                lassen: Die Regel steht dann hinten und überholt keine.
              </span>
            </div>
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}
